import cn from 'classnames';
import { useEffect, useRef, useState } from 'react';

interface QuickShootTitleProps {
	text: string;
	className?: string;
}

// Seconds
const ANIMATION_WAIT_TIME = 5;

// Pixels per second
const ANIMATION_SPEED = 35;

const Marquee = ({ text, className }: QuickShootTitleProps) => {
	const marqueeId = window.crypto.randomUUID();

	const [isOverflowing, setIsOverflowing] = useState(false);
	const containerRef = useRef<HTMLDivElement | null>(null);
	const textRef = useRef<HTMLDivElement | null>(null);
	const [textOverflow, setTextOverflow] = useState<number>(0);

	const checkOverflow = () => {
		if (containerRef.current && textRef.current) {
			const overflowPx = textRef.current.offsetWidth - containerRef.current.offsetWidth;
			setTextOverflow(overflowPx > 0 ? overflowPx : 0);
			setIsOverflowing(overflowPx > 0);
		}
	};

	useEffect(() => {
		checkOverflow();
		window.addEventListener('resize', checkOverflow);
		return () => {
			window.removeEventListener('resize', checkOverflow);
		};
	}, [text]);

	const getAnimationDuration = () => {
		return textOverflow / ANIMATION_SPEED + ANIMATION_WAIT_TIME;
	};

	const getWaitTimePercent = () => {
		return (ANIMATION_WAIT_TIME / 2 / getAnimationDuration()) * 100;
	};

	return (
		<>
			{/* Random class and animation names to avoid conflicts between multiple <Marquee> instances */}
			<style
				children={`
				.marquee-animation-${marqueeId} {
					animation: marquee-${marqueeId} ${getAnimationDuration()}s infinite alternate;
					animation-timing-function: linear;
				}
				@keyframes marquee-${marqueeId} {
					0% {
						left: 0;
					}
					${getWaitTimePercent()}% {
						left: 0;
					}
					${100 - getWaitTimePercent()}% {
						left: -${textOverflow}px;
					}
					100% {
						left: -${textOverflow}px;
					}
				}
			`}
			/>
			<div className="w-full overflow-hidden whitespace-nowrap relative" ref={containerRef}>
				{isOverflowing && <div className={cn(MarqueeStyles.marqueeShadow('left'), className)} />}
				<div className={MarqueeStyles.marqueeAnimation(isOverflowing, marqueeId)} ref={textRef}>
					{text}
				</div>
				{isOverflowing && <div className={cn(MarqueeStyles.marqueeShadow('right'), className)} />}
			</div>
		</>
	);
};

const MarqueeStyles = {
	marqueeAnimation: (isOverflowing: boolean, marqueeId: string) =>
		cn('px-[4px] inline-block will-change-left', {
			[`whitespace-nowrap overflow-visible relative min-w-[0px] marquee-animation-${marqueeId}`]: isOverflowing,
		}),
	marqueeShadow: (position: 'left' | 'right') =>
		cn('from-primary-500 to-[8px] to-transparent bg-transparent absolute top-0 bottom-0 -left-[2px] -right-[2px] z-[1]', {
			'bg-gradient-to-r': position === 'left',
			'bg-gradient-to-l': position === 'right',
		}),
};

export default Marquee;
